/**
 * The slot form's values, and turning them into the engine's `slot` field.
 *
 * Positions here are consent to act on a slot, so they are never rounded: a
 * JavaScript number is exact only up to 2^53, and a position past that is
 * refused rather than sent as a different one.
 */

import type { SlotIntent, SlotOwner } from "@/types/slotIntent";

export type { SlotIntent, SlotOwner };

export type SlotIntentForm =
  | { mode: "fresh" }
  | { mode: "takeover"; reservation: string; expectedOwner: SlotOwner }
  | { mode: "adopt"; reservation?: string; lsn: string };

const DECIMAL = /^\d+$/;
const HEX_HALVES = /^([0-9a-f]{1,8})\/([0-9a-f]{1,8})$/i;
const U64_MAX = (1n << 64n) - 1n;

/**
 * A PostgreSQL LSN, as `X/Y` in hex or as a decimal, exactly. Throws on
 * anything malformed, and on a position a JavaScript number can't hold.
 */
export function parseLsn(text: string): number {
  const value = lsnValue(text.trim());
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(
      `LSN ${text.trim()} is too large to represent exactly in the browser`,
    );
  }
  return Number(value);
}

function lsnValue(text: string): bigint {
  const halves = HEX_HALVES.exec(text);
  if (halves) {
    return (BigInt(`0x${halves[1]}`) << 32n) | BigInt(`0x${halves[2]}`);
  }
  if (!DECIMAL.test(text)) {
    throw new Error(`"${text}" is not an LSN: use X/Y in hex, or a decimal`);
  }
  const value = BigInt(text);
  if (value > U64_MAX) {
    throw new Error(`LSN ${text} is out of range for a 64-bit position`);
  }
  return value;
}

/** The `slot` field for the pipeline source; `undefined` omits it. */
export function slotIntent(form: SlotIntentForm): SlotIntent | undefined {
  switch (form.mode) {
    case "fresh":
      return undefined;
    case "takeover": {
      const reservation = form.reservation.trim();
      if (!reservation) {
        throw new Error("A takeover names the retained reservation");
      }
      return {
        takeover: { reservation, expected_owner: form.expectedOwner },
      };
    }
    case "adopt": {
      const reservation = form.reservation?.trim();
      return {
        adopt: {
          ...(reservation ? { reservation } : {}),
          expected_confirmed_flush_lsn: parseLsn(form.lsn),
        },
      };
    }
  }
}
